import React, { useState, useEffect } from 'react';
import { CircleMarker, GeoJSON, Popup } from 'react-leaflet';
import api from '@/services/api';

/**
 * Capa Geointel: amenazas naturales en tiempo casi real (sismos USGS, focos de
 * incendio NASA FIRMS, volcanes NASA EONET), servidas por el backend
 * (/api/v1/geointel/events). Autocontenida: sondea cada 2 min mientras está
 * montada; si el feature flag está apagado en el backend, no renderiza nada.
 */

export interface GeoEvent {
    id: string;
    type: 'QUAKE' | 'FIRE' | 'VOLCANO' | 'ALERT';
    lat: number;
    lon: number;
    magnitude: number | null;
    severity: string | null; // ALERT (SENAPRED): 'Roja' | 'Amarilla' | 'Verde'
    title: string;
    source: string;
    occurredAt: string;
    url: string | null;
    geometry: string | null; // GeoJSON del área afectada (ALERT: polígono comunal)
}

interface GeointelResponse {
    enabled: boolean;
    lastUpdated: string | null;
    events: GeoEvent[];
    counts: Record<string, number>;
}

const POLL_MS = 120_000;

const quakeStyle = (mag: number | null) => {
    const m = mag ?? 4;
    return {
        radius: Math.max(6, Math.min(22, m * 3)),
        color: m >= 6 ? '#dc2626' : m >= 5 ? '#f97316' : '#eab308',
        weight: 2,
        fillOpacity: 0.35,
    };
};

const GeointelLayer: React.FC = () => {
    const [events, setEvents] = useState<GeoEvent[]>([]);

    useEffect(() => {
        let alive = true;
        const load = () =>
            api.get<GeointelResponse>('/api/v1/geointel/events')
                .then(({ data }) => { if (alive && data.enabled) setEvents(data.events); })
                .catch(() => { /* capa opcional: un fallo no rompe el mapa */ });
        load();
        const timer = setInterval(load, POLL_MS);
        return () => { alive = false; clearInterval(timer); };
    }, []);

    const popup = (e: GeoEvent) => (
        <Popup>
            <div className="text-xs space-y-1">
                <div className="font-bold">{e.title}</div>
                <div>{new Date(e.occurredAt).toLocaleString()}</div>
                <div className="text-gray-500">Fuente: {e.source}</div>
                {e.url && (
                    <a href={e.url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                        Ver detalle
                    </a>
                )}
            </div>
        </Popup>
    );

    return (
        <>
            {events.map((e) => {
                // ALERT (SENAPRED) trae el polígono comunal real: se dibuja el área.
                if (e.type === 'ALERT' && e.geometry) {
                    let geom: any;
                    try { geom = JSON.parse(e.geometry); } catch { return null; }
                    const color = e.severity === 'Roja' ? '#dc2626' : '#f59e0b';
                    return (
                        <GeoJSON
                            key={e.id}
                            data={geom}
                            style={{ color, weight: 2, fillColor: color, fillOpacity: 0.15, dashArray: '4 4' }}
                        >
                            {popup(e)}
                        </GeoJSON>
                    );
                }
                const style = e.type === 'QUAKE'
                    ? quakeStyle(e.magnitude)
                    : e.type === 'FIRE'
                        ? { radius: 5, color: '#ef4444', weight: 1, fillColor: '#f97316', fillOpacity: 0.7 }
                        : e.type === 'ALERT'
                            ? { radius: 12, color: e.severity === 'Roja' ? '#dc2626' : '#f59e0b', weight: 3, fillOpacity: 0.25 }
                            : { radius: 10, color: '#a855f7', weight: 2, fillOpacity: 0.4 };
                return (
                    <CircleMarker key={e.id} center={[e.lat, e.lon]} pathOptions={style} radius={style.radius}>
                        {popup(e)}
                    </CircleMarker>
                );
            })}
        </>
    );
};

export default GeointelLayer;
